import {
  ExercisePhase,
  ExerciseResult,
} from '@pelckmans/business-components/components/exercise-manager/exercise-types';
import { ComponentProps, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ValuesOfObject } from '../../../../../@types/utils';
import {
  getLinkFromMedialink,
  isExternalMedia,
  isFile,
  isMedialinkStudentAssignment,
  isMiniDialog,
  isMiniSite,
  isScorableMedialink,
  isWordlist,
  Medialink,
  MedialinkReviewState,
  updateMedialinkReviewState,
} from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import IdentityContext from '../../../../../contexts/IdentityContext';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
// eslint-disable-next-line import/no-restricted-paths
import { Node, NodeWithoutDescription } from '../../../../../modules/material/_api/TableOfContentsApi';
// eslint-disable-next-line import/no-restricted-paths
import Score from '../../../../../modules/material/_components/common/Score';
import ExerciseModal from '../../../../../shared-components/ExerciseModal';
import { ExerciseAnswersModal } from '../../../../../shared-components/elementis/components/exercise-manager';
import MaterialAssignmentModal from '../../../../../shared-components/elementis/components/material-assignment';
import { getNodeDescription } from '../../../../../utils/getNodeDescription';
import { MedialinkDialogHeaderTags } from '../../../components/MedialinkDialogHeaderTags';
import MedialinksContext from '../../_contexts/MedialinksContext';
import MiniDialogModal from '../../components/modals/MiniDialogModal';
import WordlistModal from '../../components/modals/WordlistModal';
import useExternalMediaMedialink from './useExternalMediaMedialink';
import useFileMedialink from './useFileMedialink';
import useMiniSiteMedialink from './useMiniSiteMedialink';

export default function useMedialinks() {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;

  const { identity } = useContext(IdentityContext);
  const { medialinks, setMedialinks } = useContext(MedialinksContext);
  const { showModal, closeModal } = useContext(ModalContext);

  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { openFileMedialink } = useFileMedialink();
  const { openMiniSiteMedialink } = useMiniSiteMedialink();
  const { openExternalMediaMedialink } = useExternalMediaMedialink();
  const handleError = useThrowAsyncError();

  const getTargetAudience = (medialink: Medialink) => {
    if (identity.isTeacherAlike && medialink.group) {
      return medialink.group === 'teacher' ? 'ROLES.TEACHER' : 'ROLES.STUDENT';
    }
  };

  const getReviewState = (medialink: Medialink) => {
    const translation = t('MODULES.MATERIAL.COMPONENTS.MEDIALINKS.REVIEW.CONTROL');

    switch (medialink.reviewState) {
      case MedialinkReviewState.NOT_IN_REVIEW:
        return null;
      case MedialinkReviewState.IN_REVIEW:
        return (
          <span className="pill pill--warning" role="status">
            {translation}
          </span>
        );
      case MedialinkReviewState.ACCEPTED:
        return (
          <span className="pill pill--success" role="status">
            {translation}
          </span>
        );
      case MedialinkReviewState.DECLINED:
        return (
          <span className="pill pill--danger" role="status">
            {translation}
          </span>
        );
    }
  };

  const showScore = (medialink: Medialink) => {
    return (
      identity.isAuthenticated &&
      isScorableMedialink(medialink) &&
      ['SO', 'ES', 'VO'].includes(REACT_APP_P_STUDIO_GROUP)
    );
  };

  const getScore = (medialink: Medialink) => {
    if (!showScore(medialink)) return null;

    return <Score score={medialink.score} />;
  };

  const getDisabled = (medialink: Medialink) => {
    return (
      !medialink.file &&
      !medialink.externalMedia &&
      !medialink.edumaticExercise &&
      !medialink.eduhintExercise &&
      !medialink.assessmentQExercise &&
      !medialink.miniDialog &&
      !medialink.wordList &&
      !medialink.miniSite
    );
  };

  const updateResult = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      setMedialinks(medialinks =>
        medialinks.map(medialink => {
          if (medialink.id === medialinkId && isScorableMedialink(medialink)) {
            return {
              ...medialink,
              score: {
                amountOfItems: medialink.amountOfItems,
                ...medialink.score,
                ...result.score,
              },
            };
          }
          return medialink;
        }),
      );
    },
    [setMedialinks],
  );

  const handleSessionPaused = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      updateResult(medialinkId, result);
    },
    [updateResult],
  );

  const handleSessionFinished = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      updateResult(medialinkId, result);

      // this should later update the count of assignments indicator
    },
    [updateResult],
  );

  const openMaterial = useCallback(
    async (
      medialink: Medialink,
      nodeWithoutDescription: NodeWithoutDescription,
      selectedModule: Module,
      subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
    ) => {
      const node: Node = {
        ...nodeWithoutDescription,
        description: getNodeDescription(nodeWithoutDescription, t),
      };

      if (subLocationId) {
        await analytics.capture({
          objectId: medialink.id,
          eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
          subLocationId,
        });
      }

      if (isWordlist(medialink)) {
        return (
          <WordlistModal
            id="wordlist-modal"
            wordlistId={medialink.wordList.id}
            medialink={medialink}
            name={medialink.name}
            module={selectedModule}
            headerProps={{
              title: medialink.name,
              tags: <MedialinkDialogHeaderTags material={medialink} module={selectedModule} />,
              titleTooltip: medialink.name,
            }}
          />
        );
      }

      if (isMiniSite(medialink)) {
        return openMiniSiteMedialink(medialink, selectedModule);
      }

      if (isMiniDialog(medialink)) {
        // this id comes from the business components
        return <MiniDialogModal id="mini-dialog-modal" medialink={medialink} module={selectedModule} />;
      }

      if (isExternalMedia(medialink)) {
        return openExternalMediaMedialink(medialink, node, selectedModule);
      }

      if (isFile(medialink)) {
        return openFileMedialink(medialink, selectedModule);
      }
    },
    [analytics, openExternalMediaMedialink, openFileMedialink, openMiniSiteMedialink, t],
  );

  const openFreeExerciseOrAssignment = useCallback(
    async (
      medialink: Medialink,
      nodeWithoutDescription: NodeWithoutDescription,
      selectedModule: Module,
      onClose?: () => void,
      exerciseInitialPhase?: ExercisePhase,
      subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
    ) => {
      if (!identity.isAuthenticated) {
        // Covered by <RequireAuth />. Anonymous view no longer exists.
        return null;
      }

      if (getDisabled(medialink)) {
        // Covered my module level alerts. No need to show anything here.
        return null;
      }

      if (isScorableMedialink(medialink)) {
        if (subLocationId) {
          await analytics.capture({
            objectId: medialink.id,
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            subLocationId,
          });
        }

        return (
          <ExerciseModal
            id="exercise-modal"
            medialink={medialink}
            onSessionPaused={result => handleSessionPaused(medialink.id, result)}
            onSessionFinished={result => handleSessionFinished(medialink.id, result)}
            onClose={onClose}
            initialPhase={exerciseInitialPhase}
            selectedModule={selectedModule}
          />
        );
      }

      if (isMedialinkStudentAssignment(medialink)) {
        const currentAssignment = medialink.assignments.find(x => x.current);

        if (!currentAssignment?.id) return null;

        // This could use some refactoring, object construction is a messy
        const materialAssignment: ComponentProps<typeof MaterialAssignmentModal>['material'] = {
          ...medialink,
          type: 'medialink',
          owner: currentAssignment?.owner || currentAssignment?.user!,
          assignment: currentAssignment,
          href: getLinkFromMedialink(medialink),
        };

        const handleOpenMaterial = async () => {
          const modal = await openMaterial(medialink, nodeWithoutDescription, selectedModule, subLocationId);

          if (modal) {
            return showModal(modal);
          }
        };

        return (
          <MaterialAssignmentModal
            id="material-assignment-modal"
            material={materialAssignment}
            onOpenMaterial={handleOpenMaterial}
            onModalClosed={() => {
              if (onClose) onClose();
            }}
          />
        );
      }
      return openMaterial(medialink, nodeWithoutDescription, selectedModule, subLocationId);
    },
    [analytics, handleSessionFinished, handleSessionPaused, openMaterial, showModal, identity.isAuthenticated],
  );

  interface OpenReviewParams extends Pick<ComponentProps<typeof ExerciseAnswersModal>, 'session'> {
    medialink: Medialink;
    selectedModule: Module;
    sessionId: string;
    sessionStudentName?: string;
    subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
    nodeWithoutDescription?: NodeWithoutDescription;
  }

  const openReview = async ({
    medialink,
    selectedModule,
    sessionId,
    session,
    sessionStudentName,
    subLocationId,
    nodeWithoutDescription,
  }: OpenReviewParams) => {
    const studioGroup = process.env.REACT_APP_P_STUDIO_GROUP || 'SO';

    if (isScorableMedialink(medialink)) {
      if (subLocationId) {
        await analytics.capture({
          objectId: medialink.id,
          eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
          subLocationId,
        });
      }

      return (
        <ExerciseAnswersModal
          id="exercise-answers-modal"
          studioGroup={studioGroup}
          medialink={medialink}
          module={selectedModule}
          onClose={() => closeModal('exercise-answers-modal')}
          session={session}
          sessionId={sessionId}
          title={
            sessionStudentName
              ? t(`EXERCISE_MANAGER.EXERCISE_ANSWERS_MODAL.DIALOG_TITLE`, {
                  memberName: sessionStudentName,
                  medialinkName: medialink.name,
                })
              : medialink.name
          }
        />
      );
    }

    if (!nodeWithoutDescription) return;

    return openFreeExerciseOrAssignment(
      medialink,
      nodeWithoutDescription,
      selectedModule,
      undefined,
      undefined,
      subLocationId,
    );
  };

  const updateReviewState = async (medialinkId: string, reviewState: MedialinkReviewState, moduleId: string) => {
    try {
      await updateMedialinkReviewState(medialinkId, reviewState, moduleId);

      setMedialinks(medialinks =>
        medialinks.map(current => {
          if (current.id !== medialinkId) return current;
          return { ...current, reviewState };
        }),
      );
    } catch (err) {
      handleError(err);
    }
  };

  const updateShareInfo = async (
    medialinkId: string,
    shares: Medialink['shares'],
    assignments: Medialink['assignments'],
  ) => {
    setMedialinks(medialinks =>
      medialinks.map(current => {
        if (current.id !== medialinkId) return current;
        return { ...current, shares, assignments };
      }),
    );
  };

  return {
    medialinks,
    getTargetAudience,
    getReviewState,
    getScore, // could be removed?
    getDisabled,
    openMedialink: openFreeExerciseOrAssignment,
    openReview,
    updateReviewState,
    updateShareInfo,
  };
}
